import React, { useState, useEffect } from 'react';
import { Button, Tooltip, makeStyles } from '@material-ui/core';
import MicIcon from '../../../icons/MicIcon';
import MicOffIcon from '../../../icons/MicOffIcon';

import useLocalAudioToggle from '../../../hooks/useLocalAudioToggle/useLocalAudioToggle';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import { useSmartDeviceDetection, DeviceState } from '../../../hooks/useSmartDeviceDetection/useSmartDeviceDetection';
import { openWindowsSettingsForError, getBrowser } from '../../../utils/deviceSettings';
import { DevicePermissionModal } from '../../../components/DevicePermissionModal/DevicePermissionModal';
import { useSystemMuteNotification } from '../../../hooks/useSystemMuteNotification/useSystemMuteNotification';
import { SystemMuteToast } from '../../../components/SystemMuteToast/SystemMuteToast';

const useStyles = makeStyles(theme => ({
  toggleButton: {
    position: 'relative',
    transition: 'all 0.2s ease',
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    minWidth: '95px',
    minHeight: '50px',
  },
  errorButton: {
    backgroundColor: '#fef7e0 !important', // Light orange background like Google Meet
    borderColor: '#f9ab00 !important', // Orange border
    border: '2px solid !important',
    color: '#b06000 !important',
    '&:hover': {
      backgroundColor: '#fde293 !important',
      borderColor: '#ea8600 !important',
    },
    '&:disabled': {
      backgroundColor: '#fef7e0 !important',
      borderColor: '#f9ab00 !important',
      color: '#b06000 !important',
      opacity: 0.7,
    },
  },
  warningBadge: {
    position: 'absolute',
    top: -10,
    right: -10,
    backgroundColor: '#f9ab00', // Google Meet yellow
    color: 'white',
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 'bold',
    zIndex: 1,
    border: '2px solid white',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  tooltipContainer: {
    position: 'relative',
    '& .MuiTooltip-popper': {
      zIndex: 1500, // Ensure tooltip appears above other elements
    },
    '& .MuiTooltip-tooltip': {
      marginTop: '-1px !important', // Reduce the gap
      marginBottom: '-1px !important',
    },
  },
}));

interface EnhancedToggleAudioButtonProps {
  disabled?: boolean;
  className?: string;
  buttonText?: boolean;
}

export default function EnhancedToggleAudioButton({ disabled, className, buttonText }: EnhancedToggleAudioButtonProps) {
  const classes = useStyles();
  const [isEnabled, toggleAudioEnabled] = useLocalAudioToggle();
  const { localTracks } = useVideoContext();
  const { audioError, recheckDevice } = useSmartDeviceDetection();

  // System mute notification hook
  const {
    showAudioToast,
    showSystemMuteToast,
    hideSystemMuteToast,
    resetDismissedToasts,
  } = useSystemMuteNotification();

  // Modal state
  const [showModal, setShowModal] = useState(false);

  const hasAudioTrack = localTracks.some(track => track.kind === 'audio');
  const hasError = audioError !== null;

  // Show system mute toast when system blocking is detected
  useEffect(() => {
    if (audioError?.state === DeviceState.SYSTEM_BLOCKED) {
      showSystemMuteToast('microphone', audioError);
    }
  }, [audioError, showSystemMuteToast]);

  // Reset dismissed toasts when error is resolved
  useEffect(() => {
    if (!audioError) {
      resetDismissedToasts();
    }
  }, [audioError, resetDismissedToasts]);

  const handleClick = () => {
    if (hasError && audioError?.actionable) {
      console.log('Handling error action for state:', audioError.state);
      handleErrorAction();
    } else {
      toggleAudioEnabled();
    }
  };

  const handleErrorAction = () => {
    if (!audioError) return; // or videoError for video button

    console.log('🔧 handleErrorAction called with error:', audioError);

    switch (audioError.state) {
      case DeviceState.PERMISSION_DENIED:
        console.log('Permission denied - showing modal');
        setShowModal(true);
        break;

      case DeviceState.SYSTEM_BLOCKED:
      case DeviceState.DEVICE_UNPLUGGED:
      case DeviceState.NO_DEVICE:
      case DeviceState.DEVICE_IN_USE:
        console.log(`${audioError.state} - opening smart Windows settings`);
        if (audioError.canOpenSettings) {
          // SMART ROUTING: Pass the specific error state
          openWindowsSettingsForError('microphone', audioError.state); // or 'camera' for video
        }
        break;

      default:
        console.log('Unknown error - generic retry');
        recheckDevice('microphone'); // or 'camera' for video
    }
  };

  const handleSystemMuteLearnMore = () => {
    // Same action as clicking the button when system blocked
    if (audioError?.canOpenSettings) {
      openWindowsSettingsForError('microphone', audioError.state);
    }
  };

  const getButtonText = () => {
    if (hasError) {
      switch (audioError?.state) {
        case DeviceState.PERMISSION_DENIED:
          return 'Enable Mic';
        case DeviceState.NO_DEVICE:
          return 'No Microphone';
        case DeviceState.DEVICE_IN_USE:
          return 'Mic In Use';
        case DeviceState.DEVICE_UNPLUGGED:
          return 'Mic Disconnected';
        default:
          return 'Mic Error';
      }
    }

    // Check if we should have audio based on calling type
    const callingType = localStorage.getItem('avFilter');
    const shouldHaveAudio = callingType === 'audio' || callingType === 'video';

    if (!shouldHaveAudio) {
      return 'Audio Disabled'; // Disabled by app config
    }

    if (!hasAudioTrack && shouldHaveAudio) {
      return 'No Audio';
    }

    return isEnabled ? 'Mute' : 'Unmute';
  };

  const getTooltipText = () => {
    if (hasError) {
      return audioError?.message || 'Microphone error - click to fix';
    }
    return isEnabled ? 'Turn off microphone' : 'Turn on microphone';
  };

  // Apply error styling if there's an error
  const buttonClasses = [
    buttonText ? classes.toggleButton : classes.iconButton,
    hasError ? classes.errorButton : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <Tooltip placement="top" arrow={true} title={getTooltipText()}>
        <div className={classes.tooltipContainer}>
          <Button
            className={buttonClasses}
            onClick={handleClick}
            disabled={disabled || (audioError?.state === DeviceState.NO_DEVICE && !audioError?.actionable)}
            startIcon={isEnabled ? <MicIcon /> : <MicOffIcon />}
            data-cy-audio-toggle
          >
            {buttonText && getButtonText()}
          </Button>

          {/* Google Meet style warning badge */}
          {hasError && <div className={classes.warningBadge}>!</div>}
        </div>
      </Tooltip>

      {/* Google Meet style permission modal */}
      <DevicePermissionModal
        open={showModal}
        onClose={() => setShowModal(false)}
        deviceType="microphone"
        browser={getBrowser()}
      />

      {/* Google Meet style system mute toast */}
      <SystemMuteToast
        open={showAudioToast}
        onClose={() => hideSystemMuteToast('microphone')}
        onLearnMore={handleSystemMuteLearnMore}
        deviceType="microphone"
      />
    </>
  );
}
